import React from "react"
import * as Yup from "yup"
import {
  Box, Flex, Heading, ListItem, Text, UnorderedList,
} from "@chakra-ui/react"
import { useCurrentEvent } from "queries/events"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import RadioButtons from "components/ReactHookForm/RadioButtons"
import Select from "components/ReactHookForm/Select"

enum ImportMethods {
  Create = "create",
  UpdateByEmailAddress = "update-by-email-address",
  UpdateByGuestId = "update-by-guest-id",
}

type Props = {
  values: { updateExistingPeople: boolean, guestList: string }
  numberOfRecords: number
  onSaveImport: (updateExistingPeople: boolean, guestList: string) => void
  onClickBack: (updateExistingPeople: boolean, guestList: string) => void
  onClose: () => void
  hasGuestIdColumn: boolean
}

type FormProps = {
  guestList: string
  importMethod: ImportMethods
}

const ImportOptions = ({
  values: { updateExistingPeople, guestList },
  numberOfRecords,
  onSaveImport,
  onClickBack,
  onClose,
  hasGuestIdColumn,
}: Props) => {
  const { data: currentEvent } = useCurrentEvent()

  const guestLists = currentEvent.eventGroups.map(
    ({ id, title }) => ({ label: title, value: id.toString() }),
  )
  guestLists.unshift({ label: "No Guest List", value: "all" })

  const ImportOptionsSchema = Yup.object().shape({
    importMethod: Yup.string().required(),
    guestList: Yup.string().required(),
  })

  const createLabel = (
    <Box>
      <Text color="gray.700" fontSize="xl" fontWeight="semibold">Create new guests</Text>
      <Text color="gray.600" size="sm">Always add a new guest for each row in your file.
      </Text>
    </Box>
  )

  const updateByEmailLabel = (
    <Box>
      <Text color="gray.700" fontSize="xl" fontWeight="semibold">Update existing guests (by email address)</Text>
      <Text color="gray.600" size="sm">Match guests by their email address.
      </Text>
      <UnorderedList color="gray.600" size="xs">
        <ListItem>
          If the email exists in this event, Diobox updates that guest&apos;s information.
        </ListItem>
        <ListItem>
          If the email exists in another event, Diobox updates that record and also adds the
          guest to this event, helping you track guest history across events.
        </ListItem>
        <ListItem>
          If the email is not found, a new guest is created.
        </ListItem>
      </UnorderedList>
    </Box>
  )

  const updateByGuestIdLabel = (
    <Box>
      <Text color="gray.700" fontSize="xl" fontWeight="semibold">Update existing guests (by Guest ID)</Text>
      <Text color="gray.600" size="sm">Match guests using their unique Guest ID. This is useful if guests do
        not have email addresses or if multiple guests share the same email.
      </Text>
      <UnorderedList color="gray.600" size="xs">
        <ListItem>
          If the Guest ID matches, Diobox updates that guest&apos;s information.
        </ListItem>
        <ListItem>
          If the Guest ID doesn&apos;t match, a new guest is created.
        </ListItem>
      </UnorderedList>
    </Box>
  )

  const calculateImportMethod = () => {
    if (hasGuestIdColumn) {
      return ImportMethods.UpdateByGuestId
    }
    if (updateExistingPeople) {
      return ImportMethods.UpdateByEmailAddress
    }

    return ImportMethods.Create
  }

  const importMethodIsUpdate = (importMethod: ImportMethods) => [
    ImportMethods.UpdateByEmailAddress,
    ImportMethods.UpdateByGuestId,
  ].includes(importMethod)

  return (
    <ModalForm
      icon="import"
      size="3xl"
      initialValues={{
        importMethod: calculateImportMethod(),
        guestList,
      }}
      validationSchema={ImportOptionsSchema}
      onSubmit={(form: FormProps) => {
        onSaveImport(importMethodIsUpdate(form.importMethod), form.guestList)
      }}
      submitText="Import"
      onBack={
        (form: FormProps) => {
          onClickBack(importMethodIsUpdate(form.importMethod), form.guestList)
        }
      }
      onClose={onClose}
      closeOnEsc={false}
      title="Import Guests"
    >
      {({ control }) => (
        <>
          <Heading mb={4} size="md">Your file contains {numberOfRecords} records.</Heading>
          <Text mb={6} fontSize="lg">How do you want to handle duplicates in your file?</Text>
          <RadioButtons
            name="importMethod"
            control={control}
            options={[
              {
                label: createLabel,
                value: ImportMethods.Create,
                disabled: hasGuestIdColumn,
              },
              {
                label: updateByEmailLabel,
                value: ImportMethods.UpdateByEmailAddress,
                disabled: hasGuestIdColumn,
              },
              {
                label: updateByGuestIdLabel,
                value: ImportMethods.UpdateByGuestId,
                disabled: !hasGuestIdColumn,
              },
            ]}
            spacing={6}
          />
          <Flex mt={8} flexDirection="column" gap={1}>
            <Text fontWeight="bold">
              Tag an additional Guest List
            </Text>
            <Text mb={2}>
              Assign all imported guests to an additional Guest List for easier filtering
              after import.
            </Text>
            <Select
              vertical
              name="guestList"
              control={control}
              options={guestLists}
            />
          </Flex>
        </>
      )}
    </ModalForm>
  )
}

export default ImportOptions
